import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';

import { NavbarSkeleton, SidebarSkeleton } from '@/components/skeleton.effect';

import { RootState } from '@/redux/store';

// import { useAuthInit } from '@/hooks/useAuthInit';

const DashboardNavbar = lazy(() => import('@/pages/dashboard/components/navbar'));
const DashboardSidebar = lazy(() => import('@/pages/dashboard/components/sidebar'));

export const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const menuButtonRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { projectDetail } = useSelector((state: RootState) => state.project);

  // useAuthInit();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target as Node)
      ) {
        setSidebarOpen(false);
      }
    };

    const handleResize = () => {
      const currentWidth = window.innerWidth;
      setIsMobile(currentWidth < 768);

      if (currentWidth >= 768 && sidebarOpen) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
    };
  }, [sidebarOpen, isMobile]);
  const breadcrumbs = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    const locationState = location.state as { from?: string } | null;

    const customLabels: Record<string, string> = {
      experts: 'Experts',
      'add-expert': 'Add Expert',
      'update-expert': 'Update Expert',
      'user-roles': 'User & Roles',
      roles: 'Roles',
      'update-role': 'Update Role',
      overview: 'Overview',
      'team-members': 'Team Members',
      'suggested-experts': 'Suggested Experts',
      'suggested-tenant': 'Suggested Tenant',
    };

    if (segments[0] === 'project-detail') {
      const projectId = segments[1];
      const activePage = segments[2];
      const projectTitle = projectDetail?.id === projectId && projectDetail?.title ? projectDetail.title : 'Loading...';

      return [
        {
          label: locationState?.from === 'find-project' ? 'Find Project' : 'Projects',
          path: locationState?.from === 'find-project' ? '/find-projects' : '/projects',
        },
        {
          label: projectTitle,
          path: activePage ? `/project-detail/${projectId}/overview` : undefined,
        },
        ...(activePage
          ? [
              {
                label: customLabels[activePage] || activePage.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
              },
            ]
          : []),
      ];
    }

    if (segments[0] === 'overview') {
      return [
        {
          label: 'Overview',
          path: '/',
        },
        {
          label: 'History',
        },
      ];
    }

    return segments
      .map((segment, index) => {
        const path = `/${segments.slice(0, index + 1).join('/')}`;

        let label = customLabels[segment] || decodeURIComponent(segment);

        if (!customLabels[segment]) {
          label = label.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        }

        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(segment);
        const previousSegment = segments[index - 1];
        const isExpertIdAfterUpdate = previousSegment === 'update-expert' || previousSegment === 'update-role';

        if (isUUID && isExpertIdAfterUpdate) {
          return null;
        }

        return {
          label,
          path: index !== segments.length - 1 ? path : undefined,
        };
      })
      .filter((item) => item !== null);
  }, [location.pathname, location.state, projectDetail?.id, projectDetail?.title]);

  const isAllMembersPage = location.pathname === '/all-members' || location.pathname === '/user-roles';
  const isProjectDetailPage = location.pathname.startsWith('/project-detail');
  const isOverviewPage = location.pathname === '/';
  const isOverviewHistoryPage =
    location.pathname.startsWith('/overview/history') ||
    location.pathname.startsWith('/overview/employee-leaderboard-history') ||
    location.pathname.startsWith('/overview/best-performer-history') ||
    location.pathname.startsWith('/overview/projects-launched-history') ||
    location.pathname.startsWith('/overview/speed-of-delivery-history') ||
    location.pathname.startsWith('/overview/team-leads-performance-history');
  const shouldManageRouteScroll = isAllMembersPage || isProjectDetailPage || isOverviewHistoryPage;

  return (
    <>
      {isMobile ? (
        /* Mobile Layout */
        <div
          className={`${isProjectDetailPage ? 'flex flex-col' : 'block'} md:hidden h-screen ${shouldManageRouteScroll ? 'overflow-hidden' : 'overflow-auto'}`}
        >
          <Suspense fallback={<NavbarSkeleton />}>
            <DashboardNavbar ref={menuButtonRef} onMenuClick={() => setSidebarOpen((prev) => !prev)} breadcrumbs={breadcrumbs} />
          </Suspense>

          <div className={`${isProjectDetailPage ? 'flex flex-1 min-h-0' : 'flex h-screen'} bg-white`}>
            <Suspense fallback={<SidebarSkeleton />}>
              <DashboardSidebar isOpen={sidebarOpen} sidebarRef={sidebarRef} setIsOpen={setSidebarOpen} />
            </Suspense>

            <div className="flex-1 flex flex-col overflow-hidden bg-bg min-w-0">
              <main
                className={`flex-1 min-h-0 ${shouldManageRouteScroll ? 'overflow-hidden' : 'overflow-y-auto'} ${
                  isOverviewPage ? 'p-0' : 'px-2 py-4'
                }`}
              >
                <div className="h-full min-h-0 w-full min-w-0">
                  <Outlet />
                </div>
              </main>
            </div>
          </div>
        </div>
      ) : (
        /* Desktop & Tablet Layout */
        <div className={`w-full hidden md:flex h-screen ${shouldManageRouteScroll ? 'overflow-hidden' : 'overflow-auto'}`}>
          {/* ✅ Sidebar — */}
          <Suspense fallback={<SidebarSkeleton />}>
            <DashboardSidebar isOpen={sidebarOpen} sidebarRef={sidebarRef} className="h-full" setIsOpen={setSidebarOpen} />
          </Suspense>

          {/* ✅ Right section */}
          <div className="flex flex-1 flex-col bg-bgClr-primary min-w-0 min-h-0">
            <Suspense fallback={<NavbarSkeleton />}>
              <DashboardNavbar
                ref={menuButtonRef}
                onMenuClick={() => setSidebarOpen((prev) => !prev)}
                breadcrumbs={breadcrumbs}
              />
            </Suspense>

            {/* ✅ Fixed Scroll Section */}
            <main
              className={`flex-1 min-h-0 ${shouldManageRouteScroll ? 'overflow-hidden' : 'overflow-y-auto'} ${
                isOverviewPage ? 'p-0' : 'px-4 py-9'
              } ${isOverviewPage ? 'h-full' : 'h-auto md:h-[calc(100dvh-3rem)]'}`}
            >
              <div className="h-full min-h-0 w-full min-w-0">
                <Outlet />
              </div>
            </main>
          </div>
        </div>
      )}
    </>
  );
};
